<template>
    <div class="card mb-3 bg-elev-blue" v-if="alertHtml">
        <div class="row no-gutters">
            <div class="col-2">
                <div class="card-body">
                    <svg xmlns="http://www.w3.org/2000/svg" class="card-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path
                            fill-rule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
            </div>
            <div class="col-10">
                <div class="card-body">
                    <h5 class="card-title text-xs">
                        {{ $t('Viktig informasjon fra {office}', { office: officeName }) }}
                    </h5>
                    <div
                        ref="alertText"
                        class="card-text overflow-hidden"
                        :style="{ maxHeight: isExpanded ? null : `${collapsedHeight}px` }"
                        v-html="alertHtml"
                    ></div>
                    <button
                        v-if="showToggle"
                        class="btn btn-link btn-sm p-0 text-white"
                        type="button"
                        :aria-expanded="isExpanded.toString()"
                        @click="toggle"
                    >
                        {{ isExpanded ? $t('Se mindre') : $t('Se mer') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { GetAlertText } from '../../api/Dashboard';
    import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

    const COLLAPSED_LINES = 5;

    const alertText = ref(null);
    const alertHtml = ref('');
    const collapsedHeight = ref(0);
    const showToggle = ref(false);
    const isExpanded = ref(false);
    const officeName = ref('');
    let resizeTimer = null;

    GetAlertText().then(response => {
        officeName.value = response.data.office_name;
        alertHtml.value = response.data.alert_html;
    });

    function calculateCollapsedHeight() {
        const el = alertText.value;
        if (!el) {
            collapsedHeight.value = 0;
            showToggle.value = false;
            return;
        }

        const computedStyle = window.getComputedStyle(el);
        let lineHeight = parseFloat(computedStyle.lineHeight);
        if (!lineHeight || Number.isNaN(lineHeight)) {
            const fontSize = parseFloat(computedStyle.fontSize) || 16;
            lineHeight = fontSize * 1.3;
        }

        collapsedHeight.value = Math.round(lineHeight * COLLAPSED_LINES);
        showToggle.value = el.scrollHeight > collapsedHeight.value + 1;

        if (!showToggle.value) {
            isExpanded.value = false;
        }
    }

    function toggle() {
        isExpanded.value = !isExpanded.value;
    }

    function handleResize() {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            const wasExpanded = isExpanded.value;
            calculateCollapsedHeight();
            if (wasExpanded && showToggle.value) {
                isExpanded.value = true;
            }
        }, 100);
    }

    watch(
        alertHtml,
        () => {
            nextTick(() => calculateCollapsedHeight());
        }
    );

    onMounted(() => {
        nextTick(() => {
            calculateCollapsedHeight();
            window.addEventListener('resize', handleResize);
        });
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', handleResize);
        if (resizeTimer) clearTimeout(resizeTimer);
    });
</script>